import { CountryInput, TeachingOrgInput } from 'components/UserInputs'
import { TEACHER_UPDATE } from '../../constants'
import { useFormikContext } from 'formik'
import { ErrorMessage } from 'components/Form/ErrorMessage'
import { Box, Flex } from 'theme-ui'
import {
  AnyUserData,
  ConfigUtils,
  NetSuiteRecord,
  PageContext,
  ViewMode,
} from 'types'
import { NetSuiteLink } from 'components/NetSuiteLink'
import { FocusEvent, useEffect, useCallback } from 'react'
import { useOrgs } from 'hooks/orgHooks/useOrgs'
import { Loading } from 'components/Loading'

export type OrganizationDetailsFields =
  | 'countryCode'
  | 'forename'
  | 'surname'
  | 'defaultTeachingOrg' //3730 clean up
  | 'defaultTeachingOrgId'
  | 'teachingOrg' //3730 clean up
  | 'teachingOrgId'
  | 'netsuiteUnlinked'
  | 'netsuiteId'
  | 'netsuiteForename'
  | 'netsuiteSurname'
  | 'netsuiteEmail'
  | 'source'
export type OrganizationDetailsFormValues = Pick<
  AnyUserData,
  OrganizationDetailsFields
>

export const OrganizationDetailsSection = ({
  pageContext,
  configUtils,
  isDisabledField,
  shouldShowError = () => true,
  startTabIndex,
  isUserUsingNewOrgs,
}: {
  isUserUsingNewOrgs: boolean
  pageContext: PageContext
  viewMode: ViewMode
  configUtils: ConfigUtils
  isDisabledField: (fieldName: keyof OrganizationDetailsFormValues) => boolean
  shouldShowError?: (fieldName: keyof OrganizationDetailsFormValues) => boolean
  startTabIndex: number
}) => {
  const {
    initialValues,
    values,
    errors,
    setFieldValue,
    handleBlur,
  } = useFormikContext<OrganizationDetailsFormValues>()
  const { teachingOrg, countryCode, teachingOrgId } = values
  const [orgs, listOrgs, loading, error] = useOrgs()
  const initialTeachingOrgId = initialValues.teachingOrgId

  const orgFieldName = isUserUsingNewOrgs
    ? pageContext === TEACHER_UPDATE
      ? 'defaultTeachingOrgId'
      : 'teachingOrgId'
    : pageContext === TEACHER_UPDATE
    ? 'defaultTeachingOrg'
    : 'teachingOrg'

  const currentOrgs = isUserUsingNewOrgs
    ? orgs
    : configUtils.getTeachingOrgs(values.countryCode) //3730 clean up
  useEffect(() => {
    if (isUserUsingNewOrgs) {
      listOrgs(countryCode)
    }
  }, [listOrgs, countryCode, isUserUsingNewOrgs])
  useEffect(() => {
    if (
      isUserUsingNewOrgs &&
      pageContext !== TEACHER_UPDATE &&
      !teachingOrgId &&
      orgs?.length === 1 &&
      orgs[0].country === countryCode
    ) {
      setFieldValue('teachingOrgId', orgs[0].id)
    }
  }, [
    orgs,
    setFieldValue,
    teachingOrgId,
    pageContext,
    isUserUsingNewOrgs,
    countryCode,
  ])
  const showNetSuite =
    values.source !== 'api' &&
    pageContext !== TEACHER_UPDATE &&
    configUtils.canLinkToNetSuite(
      values.countryCode,
      isUserUsingNewOrgs ? teachingOrgId : teachingOrg
    )

  const onNetsuiteUnlink = useCallback(() => {
    setFieldValue('netsuiteUnlinked', true)
    setFieldValue('netsuiteId', undefined)
    setFieldValue('netsuiteForename', undefined)
    setFieldValue('netsuiteSurname', undefined)
    setFieldValue('netsuiteEmail', undefined)
  }, [setFieldValue])

  useEffect(() => {
    if (!showNetSuite && initialTeachingOrgId !== teachingOrgId) {
      onNetsuiteUnlink()
    }
  }, [onNetsuiteUnlink, showNetSuite, initialTeachingOrgId, teachingOrgId])

  return (
    <>
      <Box>
        <CountryInput
          label="Teaching country"
          name="countryCode"
          value={values.countryCode}
          setFieldValue={(name, value) => {
            name === 'countryCode' && setFieldValue(orgFieldName, '', false)
            setFieldValue(name, value)
          }}
          disabled={
            values.source === 'api' ? true : isDisabledField('countryCode')
          }
          error={
            shouldShowError('countryCode') ? errors.countryCode : undefined
          }
          onBlur={handleBlur}
          tabIndex={startTabIndex}
        />
      </Box>
      <Box>
        {isUserUsingNewOrgs && (
          <>
            {!!loading && (
              <Flex
                sx={{
                  height: 80,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                <Loading />
              </Flex>
            )}
            {!!error && <ErrorMessage>{error}</ErrorMessage>}
          </>
        )}
        {!!currentOrgs?.length && currentOrgs.length !== 1 ? (
          <TeachingOrgInput
            name={orgFieldName}
            label="Teaching organization"
            value={values[orgFieldName]}
            setFieldValue={setFieldValue}
            teachingOrgs={currentOrgs}
            disabled={
              values.source === 'api' ? true : isDisabledField(orgFieldName)
            }
            error={
              shouldShowError(orgFieldName) ? errors[orgFieldName] : undefined
            }
            tabIndex={startTabIndex + 1}
            onBlur={handleBlur}
          />
        ) : null}
      </Box>
      {showNetSuite && (
        <Box variant="forms.inputGroup.fullWidth" sx={{ minWidth: '100%' }}>
          <NetSuiteLink
            onSetNetsuiteRecord={(data: NetSuiteRecord) => {
              const { id, forename, surname, email } = data

              setFieldValue('forename', forename)
              setFieldValue('surname', surname)
              setFieldValue('email', email)
              setFieldValue('netsuiteForename', forename)
              setFieldValue('netsuiteSurname', surname)
              setFieldValue('netsuiteEmail', email)
              setFieldValue('netsuiteId', id)
              setFieldValue('netsuiteUnlinked', undefined)
            }}
            initialQuery={
              values.forename ? `${values.forename} ${values.surname}` : ''
            }
            disabled={isDisabledField('netsuiteId')}
            netsuiteRecord={
              values?.netsuiteEmail
                ? {
                    id: values.netsuiteId,
                    forename: values.netsuiteForename,
                    surname: values.netsuiteSurname,
                    email: values.netsuiteEmail,
                  }
                : undefined
            }
            onUnlink={onNetsuiteUnlink}
            onBlur={(evt: FocusEvent<HTMLElement>) => {
              const blurEvt = evt as FocusEvent<HTMLInputElement>
              blurEvt.target.name = 'netsuiteId'
              handleBlur(blurEvt)
            }}
            tabIndex={startTabIndex + 2}
            errorMessage={
              shouldShowError('netsuiteId') ? errors.netsuiteId : undefined
            }
          />
        </Box>
      )}
    </>
  )
}
